import React, { useState, useEffect, useCallback } from 'react';
import { users as usersApi, segments as segmentsApi, accessTypes as atApi, waivers as waiversApi, systemSettings as settingsApi } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// A user's name with a small edit button; Enter saves, Escape cancels.
function EditableName({ user, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user.display_name || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const next = value.trim();
    if (!next || next === user.display_name) { setEditing(false); setValue(user.display_name || ''); return; }
    setSaving(true);
    const ok = await onSave(user.id, next);
    setSaving(false);
    if (ok) setEditing(false);
  };

  if (!editing) {
    return (
      <span className="editable-name">
        {user.display_name}
        <button type="button" className="editable-name-btn" title="Edit name"
          onClick={() => { setValue(user.display_name || ''); setEditing(true); }}>✎</button>
      </span>
    );
  }
  return (
    <span className="editable-name editing">
      <input value={value} autoFocus maxLength={100} disabled={saving}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') { setEditing(false); setValue(user.display_name || ''); }
        }} />
      <button type="button" className="btn-sm btn-primary" onClick={save} disabled={saving}>{saving ? '…' : 'Save'}</button>
      <button type="button" className="btn-sm btn-secondary" onClick={() => { setEditing(false); setValue(user.display_name || ''); }} disabled={saving}>Cancel</button>
    </span>
  );
}

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allSegments, setAllSegments] = useState([]);
  const [allAccessTypes, setAllAccessTypes] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [waiverSignatures, setWaiverSignatures] = useState({ signatures: [], total: 0 });
  const [waiverDateRange, setWaiverDateRange] = useState({ start_date: '', end_date: '' });
  const [newAccessType, setNewAccessType] = useState({ name: '', color: '#2d6a4f' });
  const [editingAT, setEditingAT] = useState(null);
  const [settings, setSettings] = useState({});
  const [message, setMessage] = useState(null);

  const loadPending = useCallback(async () => { setPendingUsers(await usersApi.listPending()); }, []);
  const loadUsers = useCallback(async () => { setAllUsers(await usersApi.list()); }, []);
  const loadSegments = useCallback(async () => { setAllSegments(await segmentsApi.list()); }, []);
  const loadAccessTypes = useCallback(async () => { setAllAccessTypes(await atApi.list()); }, []);
  const loadWaiverSignatures = useCallback(async (params = {}) => {
    const data = await waiversApi.listSignatures(params);
    setWaiverSignatures(data);
  }, []);
  const loadSettings = useCallback(async () => {
    try { const data = await settingsApi.get(); setSettings(data.settings || {}); } catch (e) {}
  }, []);

  const loadLoginHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/users/login-history`, { credentials: 'include' });
      if (res.ok) setLoginHistory(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    loadPending(); loadUsers(); loadSegments(); loadAccessTypes(); loadSettings();
  }, [loadPending, loadUsers, loadSegments, loadAccessTypes, loadSettings]);

  useEffect(() => {
    if (activeTab === 'users') loadLoginHistory();
  }, [activeTab, loadLoginHistory]);

  useEffect(() => {
    if (activeTab === 'waivers') loadWaiverSignatures(waiverDateRange);
  }, [activeTab, loadWaiverSignatures, waiverDateRange]);

  const handleApprove = async (userId, role) => {
    try { await usersApi.approve(userId, role); setMessage(`User approved as ${role}`); loadPending(); loadUsers(); }
    catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const handleRename = async (userId, name) => {
    try {
      await usersApi.rename(userId, name);
      setMessage(`Name changed to ${name}`);
      loadPending(); loadUsers();
      return true;
    } catch (err) { setMessage(`Error: ${err.message}`); return false; }
  };

  const handleRoleChange = async (userId, role) => {
    try { await usersApi.changeRole(userId, { role }); setMessage('Role updated'); loadUsers(); }
    catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const handleToggleChair = async (userId, currentValue) => {
    try { await usersApi.changeRole(userId, { is_committee_chair: !currentValue }); setMessage('Chair status updated'); loadUsers(); }
    catch (err) { setMessage(`Error: ${err.message}`); }
  };


  // Access type handlers
  const handleCreateAT = async () => {
    if (!newAccessType.name.trim()) return;
    try {
      await atApi.create(newAccessType);
      setNewAccessType({ name: '', color: '#2d6a4f' });
      setMessage('Access type created'); loadAccessTypes();
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const handleUpdateAT = async (id) => {
    if (!editingAT) return;
    try {
      await atApi.update(id, editingAT);
      setEditingAT(null);
      setMessage('Access type updated'); loadAccessTypes();
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const handleDeleteAT = async (id, name) => {
    if (!window.confirm(`Delete access type "${name}"? This will fail if any segments are using it.`)) return;
    try {
      await atApi.delete(id);
      setMessage('Access type deleted'); loadAccessTypes();
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  return (
    <div className="admin-page">
      <h1>Admin Dashboard</h1>

      {message && (
        <div className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}<button onClick={() => setMessage(null)}>✕</button>
        </div>
      )}

      <div className="admin-tabs">
        {['pending', 'users', 'accessTypes', 'waivers', 'settings'].map((tab) => (
          <button key={tab} className={activeTab === tab ? 'active' : ''} onClick={() => setActiveTab(tab)}>
            {tab === 'pending' ? `Pending Users${pendingUsers.length > 0 ? ` (${pendingUsers.length})` : ''}` :
             tab === 'users' ? 'All Users' :
             tab === 'accessTypes' ? 'Access Types' :
             tab === 'waivers' ? 'Waivers' :
             'Settings'}
          </button>
        ))}
      </div>

      {/* ── Pending Users ──────────────────────────────────── */}
      {activeTab === 'pending' && (
        <div className="admin-section">
          {pendingUsers.length === 0 ? <p className="empty-state">No pending access requests.</p> : (
            <table className="admin-table"><thead><tr>
              <th>Name</th><th>Email</th><th>Requested</th><th>Actions</th>
            </tr></thead><tbody>
              {pendingUsers.map((u) => (
                <tr key={u.id}>
                  <td><EditableName user={u} onSave={handleRename} /></td><td>{u.email}</td>
                  <td>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="btn-sm btn-primary" onClick={() => handleApprove(u.id, 'neighbor')}>Approve as Neighbor</button>
                    <button className="btn-sm btn-secondary" onClick={() => handleApprove(u.id, 'volunteer')}>Approve as Volunteer</button>
                  </td>
                </tr>
              ))}
            </tbody></table>
          )}
        </div>
      )}

      {/* ── All Users ──────────────────────────────────────── */}
      {activeTab === 'users' && (
        <div className="admin-section">
          {/* Login Activity Histogram */}
          {loginHistory.length > 0 && (() => {
            // Aggregate logins per day (last 30 days)
            const dayMap = {};
            const now = new Date();
            for (let i = 29; i >= 0; i--) {
              const d = new Date(now);
              d.setDate(d.getDate() - i);
              dayMap[d.toISOString().slice(0, 10)] = 0;
            }
            loginHistory.forEach(r => {
              const day = r.day.slice(0, 10);
              if (day in dayMap) dayMap[day] += parseInt(r.count);
            });
            const days = Object.entries(dayMap);
            const maxCount = Math.max(...days.map(([, c]) => c), 1);
            return (
              <div style={{marginBottom: '1.5rem'}}>
                <h4 style={{fontSize: '0.9rem', marginBottom: '0.5rem'}}>Login Activity (Last 30 Days)</h4>
                <div style={{display: 'flex', alignItems: 'flex-end', gap: '2px', height: '80px'}}>
                  {days.map(([day, count]) => (
                    <div key={day} title={`${day}: ${count} login${count !== 1 ? 's' : ''}`}
                      style={{
                        flex: 1, minWidth: 0,
                        height: `${Math.max((count / maxCount) * 100, count > 0 ? 4 : 0)}%`,
                        backgroundColor: count > 0 ? 'var(--green-dark, #1b4332)' : '#e9ecef',
                        borderRadius: '2px 2px 0 0',
                      }} />
                  ))}
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#6c757d', marginTop: '2px'}}>
                  <span>{days[0][0].slice(5)}</span>
                  <span>{days[days.length - 1][0].slice(5)}</span>
                </div>
              </div>
            );
          })()}

          <table className="admin-table"><thead><tr>
            <th>Name</th><th>Email</th><th>Role</th><th>Chair</th><th>Logins</th><th>Last Login</th><th>Actions</th>
          </tr></thead><tbody>
            {allUsers.filter(u => u.status === 'active').map((u) => (
              <tr key={u.id}>
                <td><EditableName user={u} onSave={handleRename} /></td><td>{u.email}</td>
                <td>
                  <select value={u.role} onChange={(e) => handleRoleChange(u.id, e.target.value)}>
                    <option value="neighbor">Neighbor</option>
                    <option value="volunteer">Volunteer</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td><input type="checkbox" checked={u.is_committee_chair} onChange={() => handleToggleChair(u.id, u.is_committee_chair)} /></td>
                <td style={{textAlign: 'center'}}>{u.login_count || 0}</td>
                <td style={{fontSize: '0.8rem', color: '#6c757d'}}>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'}</td>
                <td><button className="btn-sm btn-danger" onClick={() => usersApi.suspend(u.id).then(loadUsers)}>Suspend</button></td>
              </tr>
            ))}
          </tbody></table>
        </div>
      )}

      {/* ── Access Agreement Types ──────────────────────────── */}
      {activeTab === 'accessTypes' && (
        <div className="admin-section">
          <h3>Manage Access Agreement Types</h3>
          <p style={{color: '#6c757d', fontSize: '0.9rem', marginBottom: '1rem'}}>
            These types are color-coded on the volunteer map view. Neighbors see all segments in a uniform color.
          </p>

          <table className="admin-table"><thead><tr>
            <th>Color</th><th>Name</th><th>Segments Using</th><th>Actions</th>
          </tr></thead><tbody>
            {allAccessTypes.map((at) => {
              const usageCount = allSegments.filter(s => s.access_type_id === at.id).length;
              const isEditing = editingAT && editingAT.id === at.id;
              return (
                <tr key={at.id}>
                  <td>
                    {isEditing ? (
                      <input type="color" value={editingAT.color} onChange={(e) => setEditingAT({ ...editingAT, color: e.target.value })} />
                    ) : (
                      <span className="color-swatch-lg" style={{ backgroundColor: at.color }} />
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input type="text" value={editingAT.name} onChange={(e) => setEditingAT({ ...editingAT, name: e.target.value })} />
                    ) : at.name}
                  </td>
                  <td>{usageCount}</td>
                  <td>
                    {isEditing ? (
                      <>
                        <button className="btn-sm btn-primary" onClick={() => handleUpdateAT(at.id)}>Save</button>
                        <button className="btn-sm btn-secondary" onClick={() => setEditingAT(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="btn-sm btn-secondary" onClick={() => setEditingAT({ id: at.id, name: at.name, color: at.color })}>Edit</button>
                        <button className="btn-sm btn-danger" onClick={() => handleDeleteAT(at.id, at.name)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody></table>

          <div className="at-create-form">
            <h4>Add New Access Type</h4>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input type="color" value={newAccessType.color} onChange={(e) => setNewAccessType({ ...newAccessType, color: e.target.value })} />
              <input type="text" placeholder="Name (e.g., Right of Way)" value={newAccessType.name}
                onChange={(e) => setNewAccessType({ ...newAccessType, name: e.target.value })}
                style={{ flex: 1, padding: '0.4rem' }} />
              <button className="btn-sm btn-primary" onClick={handleCreateAT}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Waiver Signatures ──────────────────────────────── */}
      {activeTab === 'waivers' && (
        <div className="admin-section">
          <h3>Guest Waiver Signatures</h3>

          <div className="waiver-filters">
            <label>
              From: <input type="date" value={waiverDateRange.start_date}
                onChange={(e) => setWaiverDateRange({ ...waiverDateRange, start_date: e.target.value })} />
            </label>
            <label>
              To: <input type="date" value={waiverDateRange.end_date}
                onChange={(e) => setWaiverDateRange({ ...waiverDateRange, end_date: e.target.value })} />
            </label>
            <a
              href={waiversApi.exportUrl(waiverDateRange)}
              className="btn-sm btn-primary"
              style={{ textDecoration: 'none' }}
            >
              Export CSV
            </a>
          </div>

          <p style={{ color: '#6c757d', fontSize: '0.85rem' }}>{waiverSignatures.total} total signatures</p>

          {waiverSignatures.signatures.length === 0 ? (
            <p className="empty-state">No waiver signatures found.</p>
          ) : (
            <table className="admin-table"><thead><tr>
              <th>Name</th><th>Email</th><th>Phone</th><th>Signed At</th><th>Version</th>
            </tr></thead><tbody>
              {waiverSignatures.signatures.map((sig) => (
                <tr key={sig.id}>
                  <td>{sig.guest_name}</td>
                  <td>{sig.guest_email}</td>
                  <td>{sig.guest_phone || '—'}</td>
                  <td>{new Date(sig.signed_at).toLocaleString()}</td>
                  <td>v{sig.waiver_version}</td>
                </tr>
              ))}
            </tbody></table>
          )}
        </div>
      )}

      {/* ── System Settings ────────────────────────────────── */}
      {activeTab === 'settings' && (
        <div className="admin-section">
          <h3>System Settings</h3>
          <p style={{color: '#6c757d', fontSize: '0.9rem', marginBottom: '1rem'}}>
            Configure management company and default vendor settings used in maintenance requests and notifications.
          </p>
          <div className="settings-form">
            <label>Management Company Name
              <input type="text" value={settings.mgmt_company_name || ''} onChange={e => setSettings({...settings, mgmt_company_name: e.target.value})} />
            </label>
            <label>Management Contact Name
              <input type="text" value={settings.mgmt_contact_name || ''} onChange={e => setSettings({...settings, mgmt_contact_name: e.target.value})} />
            </label>
            <label>Management Contact Email
              <input type="email" value={settings.mgmt_contact_email || ''} onChange={e => setSettings({...settings, mgmt_contact_email: e.target.value})} />
            </label>
            <label>Default Vendor Name
              <input type="text" value={settings.default_vendor_name || ''} onChange={e => setSettings({...settings, default_vendor_name: e.target.value})} />
            </label>
            <button className="btn-sm btn-primary" style={{marginTop: '1rem'}} onClick={async () => {
              try {
                await settingsApi.update(settings);
                setMessage('Settings saved');
              } catch(err) { setMessage(`Error: ${err.message}`); }
            }}>Save Settings</button>
          </div>
        </div>
      )}
    </div>
  );
}
